/**
 * Format Validators
 * Validates specific formats: email, phone, URL, UUID
 */

/**
 * Email validation regex
 * Matches standard email format: local@domain.tld
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Simple email validation regex (less strict)
 * Used for backward compatibility where needed
 */
const EMAIL_REGEX_SIMPLE = /\S+@\S+\.\S+/

/**
 * Phone validation regex
 * Supports international formats with optional country code
 */
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/

/**
 * UUID validation regex (v4 format)
 * Matches standard UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Validates email address
 * @param {string} email - Email address to validate
 * @param {boolean} strict - Use strict regex (default: true)
 * @returns {boolean} True if valid
 */
export function validateEmail(email, strict = true) {
  if (!email || typeof email !== 'string') return false
  const trimmed = email.trim()
  if (!trimmed) return false
  
  const regex = strict ? EMAIL_REGEX : EMAIL_REGEX_SIMPLE
  return regex.test(trimmed)
}

/**
 * Validates phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export function validatePhone(phone) {
  if (!phone || typeof phone !== 'string') return false
  const trimmed = phone.trim()
  if (!trimmed) return false
  
  return PHONE_REGEX.test(trimmed)
}

/**
 * Validates UUID format
 * @param {string} uuid - UUID string to validate
 * @returns {boolean} True if valid UUID format
 */
export function validateUUID(uuid) {
  if (!uuid || typeof uuid !== 'string') return false
  const trimmed = uuid.trim()
  if (!trimmed) return false
  
  return UUID_REGEX.test(trimmed)
}

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export function validateUrl(url) {
  if (!url || typeof url !== 'string') return false
  
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}
